import React, { Fragment } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Styles
import { slowTransition, fastTransition } from '../styles/effects'
import { hsla } from '../styles/colors'

// Components
import { AvatarInitials } from './Avatar'
export interface IHabit {
  color: string
  backgroundColor: string
  value: number
  hoverText?: string
}

export interface IData {
  habits: IHabit[]
  activeHabit?: number
  onHabitClick?: (index: number) => void
  image?: string
  initials?: string
  imageBackground?: string
  icon?: string
  smallVersion?: boolean
  definedSize?: number
  definedStrokeWidth?: number
  definedImageSize?: number
  foreignCenterObject?: React.ReactNode
}

interface IProps extends IData {
  className?: string
}

const CultureVisualisation: React.FC<IProps> = ({
  smallVersion,
  activeHabit,
  onHabitClick,
  habits,
  image,
  initials,
  imageBackground,
  icon,
  className,
  definedSize,
  definedStrokeWidth,
  definedImageSize,
  foreignCenterObject,
}) => {
  const { t } = useTranslation()
  const size = definedSize || (smallVersion ? 172 : 272)
  const halfSize = size / 2
  const marginSize = smallVersion ? 2 : 4
  const imageSize = definedImageSize || (smallVersion ? 36 : 56)
  // `|| 1` guards against an empty habits set (e.g. no culture values) so we
  // never divide by zero and produce an Infinite stroke width.
  const strokeWidth =
    definedStrokeWidth ||
    (size - imageSize) / 2 / (habits.length || 1) - marginSize

  const getRadius = (i: number) => {
    if (i === 0) return halfSize - strokeWidth / 2
    return halfSize - (strokeWidth * i + marginSize * i) - strokeWidth / 2
  }

  return (
    <Container
      className={className}
      height={size}
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      {habits.map((habit, index) => (
        <Fragment key={`circle-${habit.value}-${index}`}>
          <circle
            cx={halfSize}
            cy={halfSize}
            r={getRadius(index)}
            stroke={habit.backgroundColor}
            strokeWidth={strokeWidth}
            fill="none"
          />
          <FillCircle
            cx={halfSize}
            cy={halfSize}
            r={getRadius(index)}
            strokeDasharray={2 * 3.1416 * getRadius(index)}
            fullValue={((2 * 3.1416 * getRadius(index)) / 100) * 100}
            filledValue={
              ((2 * 3.1416 * getRadius(index)) / 100) * (100 - habit.value)
            }
            stroke={habit.color}
            strokeWidth={strokeWidth}
            fill="none"
            selected={activeHabit === index || activeHabit === -1}
            onClick={() => onHabitClick && onHabitClick(index)}
            clickable={
              onHabitClick !== undefined || habit.hoverText !== undefined
            }
          />
          {(onHabitClick !== undefined || habit.hoverText) && (
            <foreignObject
              width={200}
              height={32}
              style={{ pointerEvents: 'none' }}
              x={
                size -
                (strokeWidth * (index + 1) - strokeWidth / 2) -
                marginSize * index
              }
              y={halfSize - 14 / 2}
            >
              <Hover>
                {habit.hoverText ||
                  (activeHabit === index
                    ? t('cultureVisualisation.hideDetails')
                    : t('cultureVisualisation.showDetails'))}
              </Hover>
            </foreignObject>
          )}
        </Fragment>
      ))}
      {(image || initials || icon || foreignCenterObject) && (
        <foreignObject
          x={halfSize - imageSize / 2}
          y={halfSize - imageSize / 2}
          width={imageSize}
          height={imageSize}
        >
          {(initials || image) && (
            <Center backgroundColor={imageBackground}>
              {image && <Img src={image} />}
              {!image && initials && (
                <AvatarInitials>{initials}</AvatarInitials>
              )}
            </Center>
          )}
          {icon && <Icon smallVersion={smallVersion}>{icon}</Icon>}
          {foreignCenterObject && foreignCenterObject}
        </foreignObject>
      )}
    </Container>
  )
}

export default CultureVisualisation

const Container = styled.svg`
  overflow: visible;
  z-index: 1;
`

const Hover = styled.span`
  opacity: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.lightest};
  border: 1px solid ${({ theme }) => theme.colors.text};
  background-color: ${({ theme }) => hsla(theme.colors.text, 0.5)};
  border-radius: 2px;
  transition: opacity ${fastTransition};
  padding: 5px 12px 6px;
  pointer-events: none;
  z-index: 8;
`

const FillCircle = styled.circle<{
  fullValue: number
  filledValue: number
  selected: boolean
  clickable?: boolean
}>`
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  stroke-linecap: round;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: opacity ${fastTransition};
  opacity: ${({ selected }) => (selected ? 1 : 0.25)};
  position: relative;

  &:hover {
    opacity: 1;
  }

  ${({ fullValue, filledValue }) => css`
    stroke-dashoffset: ${fullValue};
    animation: ${fill(fullValue, filledValue)} ${slowTransition} 0.1s forwards;
  `};

  ${({ clickable }) =>
    clickable &&
    css`
      &:hover + foreignObject > span {
        opacity: 1;
      }
    `}
`

export const fill = (fullValue: number, filledValue: number) => keyframes`
  from {
    stroke-dashoffset: ${fullValue};
  }
  to {
    stroke-dashoffset: ${filledValue};
  }
`

const Center = styled.div<{ backgroundColor?: string }>`
  border-radius: 50%;
  width: 100%;
  height: 100%;
  display: flex;

  ${({ backgroundColor }) =>
    backgroundColor &&
    `
    background: ${backgroundColor};
  `}
`

const Img = styled.img`
  border-radius: 50%;
  width: 100%;
  height: 100%;
`

const Icon = styled.span<{ smallVersion?: boolean }>`
  font-size: ${({ smallVersion }) => (smallVersion ? 24 : 30)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`
